import { supabase } from './supabase'
import type { Database } from '@/types/database-extensions'
import { handleApiError } from '@/utils/error-handlers'

/**
 * Type representing a game from the database
 */
export type Game = Database['public']['Tables']['games']['Row']

/**
 * Creates a new game in the database
 * @param gameData - The game data to insert (excludes generated fields)
 * @returns Object containing the created game data or an error
 */
export async function createGame(gameData: Omit<Game, 'id' | 'created_at' | 'updated_at'>) {
  try {
    const { data, error } = await supabase.from('games').insert(gameData).select().single()

    if (error) throw new Error(`Failed to create game: ${error.message}`)

    return { data, error: null }
  } catch (error) {
    return handleApiError(error, 'creating the game')
  }
}

/**
 * Searches for games by name using a partial match
 * @param query - The search term to match against game names
 * @param limit - Maximum number of games to return (defaults to 10)
 * @returns Object containing the matching games or an error
 */
export async function searchGames(query: string, limit = 10) {
  try {
    const { data, error } = await supabase
      .from('games')
      .select('*')
      .ilike('name', `%${query}%`)
      .limit(limit)

    if (error) throw new Error(`Failed to search games: ${error.message}`)

    return { data, error: null }
  } catch (error) {
    return handleApiError(error, 'searching games')
  }
}

/**
 * Retrieves a game by its ID
 * @param id - The ID of the game to retrieve
 * @returns Object containing the game data or an error
 */
export async function getGameById(id: string) {
  try {
    const { data, error } = await supabase.from('games').select('*').eq('id', id).single()

    if (error) throw new Error(`Failed to fetch game: ${error.message}`)

    return { data, error: null }
  } catch (error) {
    return handleApiError(error, 'fetching the game')
  }
}
